import React, { useEffect, useMemo, useState } from "react";
import { Alert, CircularProgress, Grid, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DevopsHubHeader from "../components/devops/DevopsHubHeader";
import DevopsEntityCard from "../components/devops/DevopsEntityCard";
import { devopsService } from "../api/services/devopsService";
import { useAuth } from "../contexts/AuthContext";
import { useDevopsWorkspace } from "../contexts/DevopsWorkspaceContext";
import type { DevopsConfiguration } from "../types/devops";
import { canMaintainConfiguration } from "../utils/devopsAccess";
import { isDevopsOperator, isEnvironmentManager } from "../utils/appRoles";
import { devopsRoutes } from "../utils/devopsRoutes";

const ConfigurationsHubPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const roles = user?.roles ?? [];
  const { bindEntity } = useDevopsWorkspace();
  const [items, setItems] = useState<DevopsConfiguration[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const canAdd = isEnvironmentManager(roles) || isDevopsOperator(roles);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const configs = await devopsService.listConfigurations();
        if (!cancelled) setItems(configs);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load configurations");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) =>
      [item.name, item.scope, item.format, item.environment.name, item.owner]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [items, search]);

  return (
    <>
      <DevopsHubHeader
        title="Configurations"
        description="Every configuration belongs to one environment. Env-level content is owned by environment managers; app-level content is owned by DevOps operators. Do not store secret values."
        search={search}
        onSearchChange={setSearch}
        onAdd={canAdd ? () => navigate(devopsRoutes.configurationNew) : undefined}
        addLabel="Add configuration"
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Grid container spacing={2}>
          {filtered.map((item) => {
            const canWrite = canMaintainConfiguration(roles, item.scope);
            return (
              <Grid item xs={12} sm={6} key={item.uuid}>
                <DevopsEntityCard
                  title={item.name}
                  subtitle={`${item.scope} · ${item.format} · ${item.environment.name}`}
                  status={item.status}
                  metadata={
                    <Typography variant="caption" color="text.secondary">
                      Owner {item.owner}
                      {(item.applications ?? []).length
                        ? ` · ${(item.applications ?? []).map((app) => app.name).join(", ")}`
                        : ""}
                    </Typography>
                  }
                  onView={() => navigate(devopsRoutes.configurationView(item.uuid))}
                  onAskAgent={() =>
                    bindEntity({ type: "CONFIGURATION", uuid: item.uuid, name: item.name })
                  }
                  onEdit={canWrite ? () => navigate(devopsRoutes.configurationEdit(item.uuid)) : undefined}
                  onDelete={
                    canWrite
                      ? async () => {
                          if (!window.confirm(`Delete configuration ${item.name}?`)) return;
                          await devopsService.deleteConfiguration(item.uuid);
                          setItems((prev) => prev.filter((row) => row.uuid !== item.uuid));
                        }
                      : undefined
                  }
                />
              </Grid>
            );
          })}
          {!loading && filtered.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No configurations match your search.
              </Typography>
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

export default ConfigurationsHubPage;
